import React from 'react';
import { Checkbox, Badge, Button, Text, Tooltip } from '@fluentui/react-components';
import { Edit16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { Transaction } from '../types';
import '../styles/register.css';

interface TransactionRowProps {
  txn: Transaction;
  balance: number;
  isFuture: boolean;
  isDivider: boolean;
  onToggleCleared: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(dateStr: string): string {
  const d = dateStr.split('T')[0];
  const [year, month, day] = d.split('-');
  return `${month}/${day}/${year}`;
}

const ACTION_APPEARANCES: Record<string, 'success' | 'informative' | 'warning' | 'important' | 'subtle'> = {
  deposit: 'success',
  draft: 'informative',
  check: 'warning',
  cash: 'important',
  memo: 'subtle',
};

export const TransactionRow: React.FC<TransactionRowProps> = ({
  txn, balance, isFuture, isDivider, onToggleCleared, onEdit, onDelete,
}) => {
  const badgeAppearance = ACTION_APPEARANCES[txn.action.toLowerCase()] || 'important';

  const isMemo = txn.action === 'Memo';
  const isEom = txn.is_eom;

  const rowClasses = [
    'register-row',
    txn.cleared ? 'cleared' : '',
    isFuture ? 'future-row' : '',
    isDivider ? 'date-divider' : '',
    isMemo ? 'memo-row' : '',
    isEom ? 'eom-row' : '',
  ].filter(Boolean).join(' ');

  return (
    <tr
      className={rowClasses}
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('input, button')) return;
        onEdit(txn.id);
      }}
    >
      <td className="col-date">
        <Text size={200}>{formatDate(txn.date)}</Text>
      </td>
      <td className="col-action">
        <Badge appearance="filled" color={badgeAppearance} size="small">
          {txn.action}
        </Badge>
      </td>
      <td className="col-payee">
        <Text size={200} style={isMemo ? { fontStyle: 'italic' } : undefined}>{txn.payee}</Text>
      </td>
      <td className="col-money">{!isMemo && txn.payment ? formatMoney(txn.payment) : ''}</td>
      <td className="col-money">{!isMemo && txn.deposit ? formatMoney(txn.deposit) : ''}</td>
      <td className={`col-money col-balance ${balance < 0 ? 'negative' : ''}`}>
        {isMemo ? '' : formatMoney(balance)}
      </td>
      <td className="col-notes">
        <Text size={200}>{txn.notes || ''}</Text>
      </td>
      <td className="col-clr">
        {isMemo ? (
          isEom ? <Text size={200} style={{ color: '#3f51b5', fontWeight: 600 }}>EOM</Text> : null
        ) : (
          <Checkbox
            checked={txn.cleared}
            onChange={() => onToggleCleared(txn.id)}
          />
        )}
      </td>
      <td className="col-actions">
        <Tooltip content="Edit" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<Edit16Regular />}
            onClick={() => onEdit(txn.id)}
          />
        </Tooltip>
        <Tooltip content="Delete" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss16Regular />}
            onClick={() => onDelete(txn.id)}
          />
        </Tooltip>
      </td>
    </tr>
  );
};
